import BigNumber from 'bignumber.js'
import React, { useCallback, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { Token } from 'vvs-sdk'
import { Flex, Text, Button, Modal, LinkExternal, CalculateIcon, IconButton, Link } from 'vvs-uikit'
import { ModalActions } from 'components/Modal'
import RoiCalculatorModal from 'components/RoiCalculatorModal'
import StepIndicator from 'components/StepIndicator'
import ErrorAlert from 'components/ErrorAlert'
import { useTranslation, ContextApi } from 'contexts/Localization'
import { getFullDisplayBalance, formatNumber } from 'utils/formatBalance'
import useToast from 'hooks/useToast'
import { getInterestBreakdown } from 'utils/compoundApyHelpers'
import getFiatSymbol from 'utils/getFiatSymbol'
import getFiatValue from 'utils/getFiatValue'
import { isBlindMode } from 'utils'
import { useGetBoostedFarms, useUpdateShares } from 'hooks/boost-farm/useFarmBoostContract'
import { useTransactionAdder } from 'state/transactions/hooks'
import waitPromise from 'utils/waitPromise'

import FarmLpAmountInput from './FarmLpAmountInput'

const AnnualRoiContainer = styled(Flex)`
  cursor: pointer;
`

const AnnualRoiDisplay = styled(Text)`
  width: 72px;
  max-width: 72px;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  font-size: 14px;
`

interface DepositModalProps {
  max: BigNumber
  stakedBalance: BigNumber
  lpPrice: BigNumber
  lpLabel?: string
  onConfirm: (amount: string) => Promise<void>
  onDismiss?: () => void
  tokenName?: string
  apr?: number
  vvsApr?: number
  displayApr?: string
  addLiquidityUrl?: string
  vvsPrice?: BigNumber
  token: Token
  quoteToken: Token
  source?: string
  poolLiquidity?: BigNumber
  rewardData?: any[]
  farm?: any
  farms?: any[]
}

const DepositModal: React.FC<DepositModalProps> = ({
  max,
  stakedBalance,
  onConfirm,
  onDismiss,
  tokenName = '',
  displayApr,
  lpPrice,
  lpLabel,
  apr,
  vvsApr,
  addLiquidityUrl,
  vvsPrice,
  token,
  quoteToken,
  source,
  poolLiquidity,
  rewardData,
  farm,
  farms,
}) => {
  const [val, setVal] = useState('')
  const { toastSuccess, toastError } = useToast()
  const [running, setRunning] = useState(false)
  const [showRoiCalculator, setShowRoiCalculator] = useState(false)
  const { t } = useTranslation()
  const fullBalance = useMemo(() => {
    return getFullDisplayBalance(max)
  }, [max])

  const lpTokensToStake = new BigNumber(val)
  const fullBalanceNumber = new BigNumber(fullBalance)

  const usdToStake = lpTokensToStake.times(lpPrice)

  const interestBreakdown = getInterestBreakdown({
    principalInUSD: !lpTokensToStake.isNaN() ? usdToStake.toNumber() : 0,
    apr,
    earningTokenPrice: vvsPrice.toNumber(),
  })

  const symbol: string = getFiatSymbol()
  const annualRoi = getFiatValue(vvsPrice.times(interestBreakdown[3]))
  const formattedAnnualRoi = formatNumber(
    annualRoi.toNumber(),
    annualRoi.gt(10000) ? 0 : 2,
    annualRoi.gt(10000) ? 0 : 2,
  )
  const addTransaction = useTransactionAdder()

  const { data: boostedFarms, isLoading: isBoostedFarmsLoading } = useGetBoostedFarms()

  const { mutateAsync: updateShares, isLoading: isUpdateSharesLoading } = useUpdateShares()

  const isBoostedFarm = boostedFarms && boostedFarms.some((farmId) => farmId === farm.pid)
  const [currentStep, setCurrentStep] = useState(1)
  const stepRef = useRef<number>()
  stepRef.current = currentStep
  const [updateShareHasError, setUpdateShareHasError] = useState(false)

  const handleSelectMax = useCallback(() => {
    setVal(fullBalance)
  }, [fullBalance, setVal])

  if (showRoiCalculator) {
    return (
      <RoiCalculatorModal
        linkLabel={t('Create %lpSymbol%', { lpSymbol: lpLabel })}
        stakingTokenBalance={stakedBalance.plus(max)}
        stakingTokenSymbol={tokenName}
        stakingTokenPrice={lpPrice.toNumber()}
        earningTokenPrice={vvsPrice.toNumber()}
        apr={apr}
        vvsApr={vvsApr}
        displayApr={displayApr}
        linkHref={addLiquidityUrl}
        isFarm
        initialValue={val}
        onBack={() => setShowRoiCalculator(false)}
        poolLiquidity={poolLiquidity}
        rewardData={rewardData}
        farm={farm}
        farms={farms}
      />
    )
  }

  return (
    <Modal
      maxWidth="512px"
      title={currentStep === 1 ? t('Stake LP Tokens') : t('Update Boost Rewards')}
      onDismiss={onDismiss}
      bodyPadding="4px 24px 24px 24px"
    >
      {isBoostedFarm && (
        <StepIndicator
          running={running}
          steps={[t('Stake'), t('Update rewards')]}
          currentStep={currentStep}
          stepLabelProps={{ fontSize: '13px' }}
          mb="1.25em"
        />
      )}
      {currentStep === 1 && (
        <>
          <FarmLpAmountInput
            value={val}
            onSelectMax={handleSelectMax}
            onChange={(newValue) => setVal(newValue)}
            max={fullBalance}
            symbol={tokenName}
            addLiquidityUrl={addLiquidityUrl}
            inputTitle={t('Stake')}
            token={token}
            quoteToken={quoteToken}
            valueInUsdHint={usdToStake}
          />
          <Flex mt="24px" alignItems="center" justifyContent="space-between">
            <Text mr="8px" color="textSubtle" fontSize="14px">
              {t('Annual ROI at current rates')}:
            </Text>
            {!isBlindMode() && (
              <AnnualRoiContainer alignItems="center" onClick={() => setShowRoiCalculator(true)}>
                <AnnualRoiDisplay>{`${symbol}${formattedAnnualRoi}`}</AnnualRoiDisplay>
                <IconButton variant="text" scale="sm">
                  <CalculateIcon color="textSubtle" width="18px" />
                </IconButton>
              </AnnualRoiContainer>
            )}
          </Flex>
        </>
      )}
      {currentStep === 2 && (
        <>
          <Text>
            {t(
              'Staking LP to your boosted farm will affect boosted APR and boosted rewards, please to make sure your boost is most updated.',
            )}
          </Text>
          {updateShareHasError && (
            <ErrorAlert mt="1.5em">
              {t(
                'Your boost is not most updated, this will impact your earnings on the boosted farms. Please click "Confirm" to update your boost.',
              )}
            </ErrorAlert>
          )}
        </>
      )}
      <ModalActions>
        <Button variant="secondary" onClick={onDismiss} width="100%" disabled={running || currentStep > 1}>
          {t('Cancel')}
        </Button>
        <Button
          width="100%"
          disabled={
            running ||
            !lpTokensToStake.isFinite() ||
            lpTokensToStake.eq(0) ||
            lpTokensToStake.gt(fullBalanceNumber) ||
            isBoostedFarmsLoading ||
            isUpdateSharesLoading
          }
          onClick={async () => {
            setRunning(true)
            setUpdateShareHasError(false)
            try {
              if (stepRef.current === 1) {
                await onConfirm(val)
                toastSuccess(t('Staked!'), renderStakedToastContent(source, t))

                if (isBoostedFarm) {
                  setCurrentStep(2)
                  await waitPromise(3000)
                }
              }

              if (isBoostedFarm && stepRef.current === 2) {
                const { tx } = await updateShares({ farmId: farm.pid })
                addTransaction(tx, {
                  title: t('Your staked liquidity is updated successfully!'),
                  summary: t('Your boost apr will be optimised and adjusted accordingly'),
                })
                setCurrentStep(3)
                await waitPromise(2000)
              }

              onDismiss()
            } catch (e) {
              console.error(e)
              switch (stepRef.current) {
                case 1:
                  toastError(
                    t('Error'),
                    t(
                      'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
                    ),
                  )
                  break
                case 2:
                  setUpdateShareHasError(true)
                  toastError(
                    t('Error in updating shares'),
                    t('Network is congested or you have rejected the transaction'),
                  )
                  break
                default:
                  toastError(t('Error'), t(e.message))
              }
            } finally {
              setRunning(false)
            }
          }}
        >
          {running ? t('Confirming...') : t('Confirm')}
        </Button>
      </ModalActions>
      {currentStep === 1 && (
        <LinkExternal href={addLiquidityUrl} style={{ alignSelf: 'center' }}>
          {t('Create %lpSymbol%', { lpSymbol: tokenName })}
        </LinkExternal>
      )}
    </Modal>
  )
}

export default DepositModal

const renderStakedToastContent = (source: string, t: ContextApi['t']) => (
  <>
    <Text mr="8px" color="textSubtle" fontSize="14px">
      {t('Your funds have been staked in the farm')}
    </Text>
    {source && source === 'zap' ? (
      <Link external href={`${process.env.REACT_APP_BASE_URL}/dashboard/farm`}>
        <Text fontSize="13px" fontWeight="400" color="primary">
          View your Farms
        </Text>
      </Link>
    ) : null}
  </>
)
